/**
 * HeroMinimal Section
 * Минималистичный hero: только текст на цветном/градиентном фоне
 */
import { Text, Button, Container } from '../components';

const heightClasses = {
  small: 'min-h-[300px] md:min-h-[350px]',
  medium: 'min-h-[400px] md:min-h-[450px]',
  large: 'min-h-[500px] md:min-h-[550px]',
  fullscreen: 'min-h-screen',
};

const alignmentClasses = {
  left: 'text-left items-start',
  center: 'text-center items-center',
  right: 'text-right items-end',
};

const buttonsAlignmentClasses = {
  left: 'justify-start',
  center: 'justify-center',
  right: 'justify-end',
};

const contentAlignmentClasses = {
  left: '',
  center: 'mx-auto',
  right: 'ml-auto',
};

// Предустановленные градиенты
const gradientPresets = {
  'blue-purple': 'linear-gradient(135deg, #667eea 0%, #764ba2 100%)',
  'green-teal': 'linear-gradient(135deg, #11998e 0%, #38ef7d 100%)',
  'orange-red': 'linear-gradient(135deg, #f093fb 0%, #f5576c 100%)',
  'dark-blue': 'linear-gradient(135deg, #0f2027 0%, #203a43 50%, #2c5364 100%)',
  'sunset': 'linear-gradient(135deg, #ff6b6b 0%, #feca57 100%)',
  'ocean': 'linear-gradient(135deg, #2193b0 0%, #6dd5ed 100%)',
  'midnight': 'linear-gradient(135deg, #232526 0%, #414345 100%)',
  'custom': null, // будет использоваться customGradient
};

function getBackgroundStyle(data, styles) {
  const backgroundType = data?.backgroundType || 'solid';

  if (backgroundType === 'gradient') {
    const preset = data?.gradientPreset || 'blue-purple';
    if (preset === 'custom' && data?.customGradient) {
      return { background: data.customGradient };
    }
    return { background: gradientPresets[preset] || gradientPresets['blue-purple'] };
  }

  // solid
  return { backgroundColor: styles?.backgroundColor || data?.backgroundColor || '#1a1a2e' };
}

export default function HeroMinimalSection({ data, styles }) {
  const alignment = data?.alignment || 'center';
  const height = data?.height || 'medium';
  const buttons = data?.buttons || [];

  const backgroundStyle = getBackgroundStyle(data, styles);

  return (
    <section
      className={`relative ${heightClasses[height] || heightClasses.medium}`}
      style={{
        ...backgroundStyle,
        paddingTop: styles?.paddingTop ?? 64,
        paddingBottom: styles?.paddingBottom ?? 64,
      }}
    >
      {/* Content */}
      <div className={`relative h-full flex flex-col justify-center ${alignmentClasses[alignment] || alignmentClasses.center} px-4`}>
        <Container>
          <div className={`max-w-3xl ${contentAlignmentClasses[alignment] || 'mx-auto'}`}>
            {/* Заголовок */}
            <Text
              as="h1"
              data={typeof data?.title === 'object' ? data.title : { text: data?.title }}
              defaults={{ size: '5xl', weight: 'bold', color: 'white' }}
              className="mb-4 md:mb-6"
            />

            {/* Подзаголовок */}
            {(data?.subtitle?.text || data?.subtitle) && (
              <Text
                as="p"
                data={typeof data?.subtitle === 'object' ? data.subtitle : { text: data?.subtitle }}
                defaults={{ size: 'xl', weight: 'normal', color: 'white-muted' }}
                className="mb-8 md:mb-10"
              />
            )}

            {buttons.length > 0 && (
              <div className={`flex flex-col sm:flex-row gap-3 md:gap-4 ${buttonsAlignmentClasses[alignment] || buttonsAlignmentClasses.center}`}>
                {buttons.map((button, index) => {
                  const textData = typeof button.text === 'object' ? button.text : { text: button.text || '' };
                  return (
                    <Button
                      key={button.id || index}
                      href={button.link || '#'}
                      variant={button.variant === 'primary' ? 'white' : 'outline-white'}
                      size="lg"
                    >
                      <Text
                        as="span"
                        data={textData}
                        defaults={{ size: 'base', weight: 'medium', color: 'inherit' }}
                      />
                    </Button>
                  );
                })}
              </div>
            )}
          </div>
        </Container>
      </div>
    </section>
  );
}
